import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import { toast } from "sonner";
import { strengthLabel, MEMORY_TYPE_LABEL, MEMORY_LAYER_LABEL, PROVENANCE_LABEL, forgettingLabel } from "@/lib/memory-vocab";
import { ArrowLeft, Trash2, ArrowUpCircle, AlertTriangle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

export default function MemoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [forgetReason, setForgetReason] = useState("");

  const { data: memory, isLoading } = useQuery({
    queryKey: ["memory-detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consolidated_memories")
        .select("*")
        .eq("id", id!).maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const retrievals: any[] = []; // memory_retrieval_trace não indexa por memory_id; histórico virá em sprint futura

  const forgetMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("memory-forget", {
        body: { memory_id: id, reason: forgetReason },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success("Memória esquecida. Pode ser restaurada em até 30 dias.");
      qc.invalidateQueries({ queryKey: ["memory-list"] });
      navigate("/memory");
    },
    onError: (e: any) => toast.error(e?.message || "Falha ao esquecer"),
  });

  if (isLoading) return <div className="container mx-auto p-6"><Skeleton className="h-96" /></div>;
  if (!memory) return <div className="container mx-auto p-6 text-center text-muted-foreground">Memória não encontrada.</div>;

  const s = strengthLabel(memory.confidence);

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate("/memory")} className="gap-2">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-lg">{memory.content}</CardTitle>
            <div className="flex gap-1">
              <Badge variant="outline">{MEMORY_LAYER_LABEL[memory.memory_layer] || memory.memory_layer}</Badge>
              <Badge variant="secondary">{MEMORY_TYPE_LABEL[memory.memory_type] || memory.memory_type}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Força da memória ({s.label})</span><span>{s.pct}%</span>
            </div>
            <Progress value={s.pct} className="h-2" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <Stat label="Origem" value={PROVENANCE_LABEL[memory.provenance_kind || "consolidated"]} />
            <Stat label="Esquecimento" value={forgettingLabel(memory.decay_rate)} />
            <Stat label="Acessos" value={String(memory.access_count ?? 0)} />
            <Stat label="Reforços" value={String(memory.reinforced_by_count ?? 0)} />
            <Stat label="Criada" value={formatDistanceToNow(new Date(memory.created_at), { locale: ptBR, addSuffix: true })} />
            <Stat label="Último acesso" value={memory.last_accessed_at ? formatDistanceToNow(new Date(memory.last_accessed_at), { locale: ptBR, addSuffix: true }) : "—"} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Histórico de uso</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {memory.access_count ?? 0} acesso(s) registrado(s). Detalhamento por consulta virá em atualização futura.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Ações</CardTitle></CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm" className="gap-2">
                <Trash2 className="w-4 h-4" /> Esquecer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Esquecer esta memória?</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                Pode ser restaurada em até 30 dias. Após isso, é apagada definitivamente.
              </p>
              <Textarea
                placeholder="Motivo (mínimo 5 caracteres)"
                value={forgetReason}
                onChange={e => setForgetReason(e.target.value)}
                rows={3}
              />
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={forgetReason.trim().length < 5 || forgetMutation.isPending}
                  onClick={() => forgetMutation.mutate()}
                >
                  Confirmar esquecimento
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button variant="outline" size="sm" className="gap-2" disabled>
            <ArrowUpCircle className="w-4 h-4" /> Promover a oficial
          </Button>
          <Button variant="outline" size="sm" className="gap-2" disabled>
            <AlertTriangle className="w-4 h-4" /> Marcar contradição
          </Button>
          <span className="text-xs text-muted-foreground self-center">
            Promover/contradizer disponível na próxima atualização (Memory-04).
          </span>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
